import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { fmtDate } from "../../lib/status";
import { KeyRound, Gauge, Fuel, CheckCircle2, ArrowUpRight } from "lucide-react";
import PickupHandoverWizard from "./PickupHandoverWizard";

const FUEL_LABEL = { 0: "Empty", 25: "1/4", 50: "1/2", 75: "3/4", 100: "Full" };

/**
 * Self-contained pickup-handover summary card for BookingDetail.jsx -
 * mirrors the ConfirmationHistoryPanel/BookingTimeline convention (fetches
 * by bookingId, renders nothing until it has something worth showing).
 */
const DEPOSIT_STATUS_LABEL = {
  not_required: "Not required", pending: "Pending", received: "Received",
  refund_pending: "Refund pending", refunded: "Refunded",
  partially_withheld: "Partially withheld", fully_withheld: "Fully withheld",
};

export default function PickupHandoverPanel({ bookingId, onChanged }) {
  const [handover, setHandover] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deposit, setDeposit] = useState(null);
  const [termsVersion, setTermsVersion] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/pickup-handover`);
      setHandover(data);
    } catch (_) {
      setHandover(null); // no handover started yet, not an error
    }
    // Phase 2H: informational only (spec R): never blocks pickup, just
    // gives staff visibility into deposit/terms readiness at this moment.
    try {
      const { data } = await api.get(`/bookings/${bookingId}/deposit`);
      setDeposit(data);
    } catch (_) { /* ignore */ }
    try {
      const { data } = await api.get(`/bookings/${bookingId}/confirmation-versions`);
      const confirmed = (data.versions || []).filter((v) => v.confirmed_at && v.terms_version_number);
      setTermsVersion(confirmed.length ? confirmed[confirmed.length - 1].terms_version_number : null);
    } catch (_) { /* ignore */ }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  if (!handover) return null;
  const insp = handover.inspection;
  const inProgress = handover.status === "in_progress";

  return (
    <div className="rounded-lg border border-slate-200 p-4" data-testid="pickup-handover-panel">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="font-display text-base font-semibold inline-flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-[#1B4332]" /> Pickup Handover
        </h3>
        {inProgress ? (
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            In progress
          </span>
        ) : (
          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Completed
          </span>
        )}
      </div>

      {(deposit || termsVersion) && (
        <p className="text-xs text-slate-500 mb-2" data-testid="pickup-handover-deposit-terms-line">
          {deposit && <>Deposit: {DEPOSIT_STATUS_LABEL[deposit.status] || deposit.status}</>}
          {deposit && termsVersion && " · "}
          {termsVersion && <>Terms: v{termsVersion} accepted</>}
        </p>
      )}

      {!inProgress && (
        <dl className="text-sm space-y-1.5">
          <Row label="Actual pickup" value={fmtDate(handover.actual_pickup_datetime)} />
          <Row label="Completed by" value={handover.completed_by_name || "Staff"} />
          {insp?.odometer_km != null ? (
            <Row icon={<Gauge className="w-3.5 h-3.5" />} label="Odometer" value={`${insp.odometer_km.toLocaleString()} km`} />
          ) : (
            <Row icon={<Gauge className="w-3.5 h-3.5" />} label="Odometer"
                 value={`Not recorded${handover.odometer_skip_reason ? ` (${handover.odometer_skip_reason.replace(/_/g, " ")})` : ""}`} />
          )}
          {insp?.fuel_level != null && (
            <Row icon={<Fuel className="w-3.5 h-3.5" />} label="Fuel" value={FUEL_LABEL[insp.fuel_level] ?? `${insp.fuel_level}%`} />
          )}
          <Row label="Existing damage" value={handover.existing_damage_reviewed ? "Reviewed before release" : "Not reviewed"} />
        </dl>
      )}

      {inProgress && (
        <button onClick={() => setWizardOpen(true)} data-testid="pickup-handover-resume-btn"
                className="mt-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#1B4332] text-[#1B4332] bg-white hover:bg-emerald-50 text-sm">
          Resume handover <ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      )}

      <PickupHandoverWizard bookingId={bookingId} open={wizardOpen}
                             onClose={() => setWizardOpen(false)}
                             onDone={() => { load(); onChanged?.(); }} />
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500 inline-flex items-center gap-1">{icon}{label}</dt>
      <dd className="font-medium text-slate-900 text-right">{value}</dd>
    </div>
  );
}
